import { FC, useEffect, useMemo, useState } from "react";
import { Icon, IconName } from "Components/UI";
import Button from "Components/UI/Button";
import {
  ConstructionSiteDisciplineLibraryEnum,
  IConstructionSiteDisciplineLibrary,
  IConstructionSiteDisciplineLibraryItem,
} from "Data/interfaces/Obra/IDisciplineLibraries";
import {
  Body,
  BodyContent,
  Children,
  Container,
  Content,
  Footer,
  HeaderContent,
  Hr,
  Left,
  Main,
  MainContent,
  Section,
  Top,
} from "./styles";

interface OptionsDefault {
  title: string;
  children: IConstructionSiteDisciplineLibrary[];
}

interface ILibrary {
  loading?: boolean;
  listLoading?: boolean;
  libraries: IConstructionSiteDisciplineLibrary[];
  onOpen: (lib: IConstructionSiteDisciplineLibrary) => void;
  onDelete: (lib: IConstructionSiteDisciplineLibrary) => void;
}

const iconNameTypeEnum: Record<ConstructionSiteDisciplineLibraryEnum, IconName> = {
  [ConstructionSiteDisciplineLibraryEnum.Standard]: 'coordly',
  [ConstructionSiteDisciplineLibraryEnum.MarketStandard]: 'abnt',
  [ConstructionSiteDisciplineLibraryEnum.ConstructionSite]: 'menuContexto',
};

const Library: FC<ILibrary> = ({
  libraries,
  onOpen,
  onDelete,
}) => {
  const [active, setActive] = useState(0);
  const [activeType, setActiveType] = useState<ConstructionSiteDisciplineLibraryEnum | null>(null);

  const options = useMemo(() => {
    const data: OptionsDefault[] = [];

    if (libraries && libraries.length > 0) {
      const standardLibraries = libraries.filter(lib => lib.Type === ConstructionSiteDisciplineLibraryEnum.Standard);
      const marketLibraries = libraries.filter(lib => lib.Type === ConstructionSiteDisciplineLibraryEnum.MarketStandard);

      if (standardLibraries.length > 0) {
        const opt: OptionsDefault = {
          title: 'Selecione um padrão de disciplinas',
          children: []
        };

        for (const lib of standardLibraries) {
          opt.children.push(lib);
        }

        data.push(opt);
      }

      if (marketLibraries.length > 0) {
        const opt: OptionsDefault = {
          title: 'Padrões do mercado',
          children: []
        };

        for (const lib of marketLibraries) {
          opt.children.push(lib);
        }

        data.push(opt);
      }
    }

    return data;
  }, [libraries]);

  const optionActive = useMemo(() => {
    return libraries.find(opt => {
      return opt.ConstructionSiteDisciplineLibraryId === active && opt.Type === activeType;
    });
  }, [active, activeType, libraries]);

  const optionActiveChildren = useMemo(() => {
    let dataDefault: IConstructionSiteDisciplineLibraryItem[] = [];

    if (optionActive && optionActive?.Items && optionActive.Items?.length > 0) {
      dataDefault = optionActive.Items.sort((a, b) => a.Order - b.Order);
    }

    return {
      data: dataDefault,
    };
  }, [optionActive]);

  useEffect(() => {
    if (libraries && libraries.length > 0) {
      const standardLibrary = libraries.find(lib => {
        return lib.Type === ConstructionSiteDisciplineLibraryEnum.Standard;
      });
      const current = standardLibrary || libraries[0];

      setActive(current.ConstructionSiteDisciplineLibraryId);
      setActiveType(current.Type);
    }
  }, [libraries]);

  return (
    <div>
      <Container>
        <Content>
          <Left>
            {options.map((opt, index) => (
              <Section key={opt.title}>
                <span className="title">{opt.title}</span>
                {opt.children.map(item => (
                  <Children
                    key={item.ConstructionSiteDisciplineLibraryId}
                    isactive={active === item.ConstructionSiteDisciplineLibraryId && activeType === item.Type}
                    onClick={() => {
                      setActive(item.ConstructionSiteDisciplineLibraryId);
                      setActiveType(item.Type);
                    }}
                  >
                    {(item.IconName ||
                      item.Type === ConstructionSiteDisciplineLibraryEnum.Standard ||
                      item.Type === ConstructionSiteDisciplineLibraryEnum.MarketStandard) && (
                        <Icon
                          icon={item.IconName || iconNameTypeEnum[item.Type]}
                        />
                      )}
                    <span className="titleChildren">{item.Name}</span>
                  </Children>
                ))}
                {index !== options.length - 1 && <Hr />}
              </Section>
            ))}
          </Left>

          <Main>
            {optionActive && (
              <Top>
                <span className="title">{optionActive.Name}</span>
                <span className="description">{optionActive?.Description}</span>
              </Top>
            )}
            <MainContent>
              <HeaderContent>
                <span className="columnIcon">Ícone</span>
                <span className="columnSigla">Sigla</span>
                <span className="columnDiscipline">Disciplina</span>
              </HeaderContent>
              <Body>
                <BodyContent>
                  {optionActiveChildren.data.map(opt => (
                    <span key={opt.ConstructionSiteDisciplineLibraryItemId} className="row">
                      <span className="rightRow">
                        <span className="columnIcon">
                          <Icon icon={opt.Icon || 'menuContexto'} customSize={16} className="rowIcon" />
                        </span>
                        <span className="columnSigla">{opt.Acronym}</span>
                        <span className="columnDiscipline">{opt.Name}</span>
                      </span>
                    </span>
                  ))}
                </BodyContent>
              </Body>
              <Footer>
                <Button
                  type="primary"
                  className="btnFooter"
                  onClick={() => !!optionActive
                    ? onOpen(optionActive)
                    : undefined
                  }
                >
                  Editar
                </Button>
                <Button
                  type="primary"
                  danger
                  className="btnFooter btnFooterDanger"
                  onClick={() => !!optionActive
                    ? onDelete(optionActive)
                    : undefined
                  }
                >
                  Excluir
                </Button>
              </Footer>
            </MainContent>
          </Main>
        </Content>
      </Container>
    </div>
  )
}

export default Library;
